/**
 * Document Chat Component
 * 
 * Allows users to ask questions about documents in a conversational way
 * Example: "Which file contains ISO 27001 policy?"
 */

import React, { useState, useRef, useEffect } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaSearch } from 'react-icons/fa';
import { Document } from './types';
import documentService from './documentService';
import './DocumentChat.css';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  documents?: Document[];
  timestamp: Date;
}

interface DocumentChatProps {
  onDocumentClick?: (documentId: string) => void;
}

const DocumentChat: React.FC<DocumentChatProps> = ({ onDocumentClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const extractSearchTerms = (query: string): string => {
    // Remove common question words and extract key terms
    const questionWords = /^(which|what|where|when|who|how|show|find|search|list|get|tell|me|about|contains|contain|has|have|file|files|document|documents)\s+/i;
    let searchTerm = query.replace(questionWords, '').trim();
    
    // Remove trailing question marks and common phrases
    searchTerm = searchTerm.replace(/\?+$/, '').trim();
    searchTerm = searchTerm.replace(/\s+(file|files|document|documents)$/i, '').trim();
    
    return searchTerm || query; // Fallback to original if nothing extracted
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Extract search terms from the query
      const searchTerm = extractSearchTerms(input.trim());
      
      // Search documents using the existing API
      const documents = await documentService.getDocuments({
        searchTerm: searchTerm,
      });

      // Generate a response based on results
      let responseContent = '';
      if (documents.length === 0) {
        responseContent = `I couldn't find any documents matching "${searchTerm}". Try searching with different keywords or check your filters.`;
      } else if (documents.length === 1) {
        responseContent = `I found 1 document that matches your query:\n\n**${documents[0].metadata.title}**\n${documents[0].metadata.description ? documents[0].metadata.description.substring(0, 150) + '...' : 'No description available'}`;
      } else {
        responseContent = `I found ${documents.length} documents matching your query:`;
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: responseContent,
        documents: documents.length > 0 ? documents : undefined,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `Sorry, I encountered an error while searching: ${error.message || 'Unknown error'}. Please try again.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date): string => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) {
    return (
      <button
        className="document-chat-toggle"
        onClick={() => setIsOpen(true)}
        title="Ask about documents"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#0d6efd',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          zIndex: 1000,
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
      >
        <FaComments />
      </button>
    );
  }

  return (
    <div
      className="document-chat-container"
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '400px',
        maxWidth: '90vw',
        height: '600px',
        maxHeight: '80vh',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#0d6efd',
          color: 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaComments />
          <h6 style={{ margin: 0, fontWeight: '600' }}>Document Assistant</h6>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px 8px',
          }}
        >
          <FaTimes />
        </button>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: '#6c757d',
              padding: '20px',
            }}
          >
            <FaSearch size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>
              Ask me about your documents!<br />
              <small>Example: "Which file contains ISO 27001 policy?"</small>
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '80%',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: message.type === 'user' ? '#0d6efd' : '#f8f9fa',
                color: message.type === 'user' ? 'white' : '#212529',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '14px',
                lineHeight: '1.5',
              }}
            >
              {message.content}
            </div>
            
            {message.documents && message.documents.length > 0 && (
              <div
                style={{
                  marginTop: '8px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {message.documents.slice(0, 5).map((doc) => (
                  <div
                    key={doc.metadata.id}
                    onClick={() => {
                      if (onDocumentClick) {
                        onDocumentClick(doc.metadata.id);
                      }
                    }}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.borderColor = '#0d6efd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                      {doc.metadata.title}
                    </div>
                    {doc.metadata.description && (
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                        {doc.metadata.description.length > 100
                          ? doc.metadata.description.substring(0, 100) + '...'
                          : doc.metadata.description}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#e9ecef',
                          color: '#495057',
                        }}
                      >
                        {doc.metadata.category}
                      </span>
                      {doc.metadata.framework && (
                        <span
                          style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: '#cfe2ff',
                            color: '#084298',
                          }}
                        >
                          {doc.metadata.framework}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {message.documents.length > 5 && (
                  <div style={{ fontSize: '12px', color: '#6c757d', textAlign: 'center', marginTop: '4px' }}>
                    ...and {message.documents.length - 5} more document(s)
                  </div>
                )}
              </div>
            )}

            <small
              style={{
                marginTop: '4px',
                color: '#6c757d',
                fontSize: '11px',
              }}
            >
              {formatTime(message.timestamp)}
            </small>
          </div>
        ))}

        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6c757d' }}>
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              style={{ width: '16px', height: '16px', borderWidth: '2px' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ fontSize: '14px' }}>Searching documents...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: '12px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          gap: '8px',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask about documents..."
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          style={{
            padding: '10px 16px',
            backgroundColor: '#0d6efd',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
            opacity: input.trim() && !isLoading ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

export default DocumentChat;

